// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  UserGroupIcon,
  UserIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { alertsAPI, authAPI } from '../services/api';
import { RoleInfoMap, Roles } from '../config/rolesConfig';

// Types
interface NotificationRecipient {
  id?: string;
  user_id?: string;
  user_email?: string;
  role?: string;
  channels: string[];
}

interface User {
  id: string;
  email: string;
  username: string;
  role: string;
}

interface NotificationRule {
  id: string;
  name: string;
  description?: string;
  priority: number;
  is_active: boolean;
  severity_filter?: string[] | null;
  category_filter?: string[] | null;
  alert_type_filter?: string[] | null;
  site_filter?: string[] | null;
  division_filter?: string[] | null;
  stop_processing: boolean;
  recipients: NotificationRecipient[];
}

// Constants
const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'info'];
const CATEGORY_OPTIONS = ['shipment', 'capacity', 'delay', 'inventory', 'system', 'compliance'];
const CHANNEL_OPTIONS = ['in_app', 'email', 'push', 'sms'];
const ROLE_OPTIONS = Object.values(Roles);

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  info: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const categoryColors: Record<string, string> = {
  shipment: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  capacity: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  delay: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  inventory: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  system: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
  compliance: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
};

const channelLabels: Record<string, string> = {
  in_app: 'In-App',
  email: 'Email',
  push: 'Push',
  sms: 'SMS',
};

interface AlertNotificationRulesProps {
  onClose?: () => void;
}

const AlertNotificationRules: React.FC<AlertNotificationRulesProps> = ({ onClose }) => {
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<NotificationRule>>({
    name: '',
    description: '',
    priority: 100,
    is_active: true,
    severity_filter: null,
    category_filter: null,
    stop_processing: false,
    recipients: [],
  });

  // Load rules and users on mount
  useEffect(() => {
    loadRules();
    loadUsers();
  }, []);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await alertsAPI.notificationRules();
      setRules(response.data.rules || []);
    } catch (err) {
      console.error('Failed to load notification rules:', err);
      setError('Failed to load notification rules');
      // Use mock data as fallback
      setRules([
        {
          id: 'mock-1',
          name: 'Critical Alerts - All Admins',
          description: 'Route all critical alerts to administrators',
          priority: 10,
          is_active: true,
          severity_filter: ['critical'],
          category_filter: null,
          alert_type_filter: null,
          site_filter: null,
          division_filter: null,
          stop_processing: false,
          recipients: [
            { role: 'superuser', channels: ['in_app', 'email', 'push'] },
            { role: 'admin', channels: ['in_app', 'email', 'push'] },
          ],
        },
        {
          id: 'mock-2',
          name: 'High Priority Alerts',
          description: 'Route high severity alerts to managers',
          priority: 20,
          is_active: true,
          severity_filter: ['high'],
          category_filter: null,
          alert_type_filter: null,
          site_filter: null,
          division_filter: null,
          stop_processing: false,
          recipients: [
            { role: 'admin', channels: ['in_app', 'email'] },
            { role: 'transport_manager', channels: ['in_app', 'email'] },
          ],
        },
        {
          id: 'mock-3',
          name: 'Shipment Alerts',
          description: 'Route shipment-related alerts to transport team',
          priority: 30,
          is_active: true,
          severity_filter: null,
          category_filter: ['shipment'],
          alert_type_filter: null,
          site_filter: null,
          division_filter: null,
          stop_processing: false,
          recipients: [
            { role: 'transport_manager', channels: ['in_app', 'email'] },
          ],
        },
        {
          id: 'mock-4',
          name: 'Capacity Alerts',
          description: 'Route capacity warnings to planners',
          priority: 40,
          is_active: true,
          severity_filter: null,
          category_filter: ['capacity'],
          alert_type_filter: null,
          site_filter: null,
          division_filter: null,
          stop_processing: false,
          recipients: [
            { role: 'planner', channels: ['in_app', 'email'] },
            { role: 'site_manager', channels: ['in_app'] },
          ],
        },
        {
          id: 'mock-5',
          name: 'Default - All Users',
          description: 'Catch-all rule for remaining alerts',
          priority: 999,
          is_active: true,
          severity_filter: null,
          category_filter: null,
          alert_type_filter: null,
          site_filter: null,
          division_filter: null,
          stop_processing: false,
          recipients: [
            { role: 'viewer', channels: ['in_app'] },
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await authAPI.listUsers();
      const data = response.data;
      const mappedUsers: User[] = (data.users || []).map((u: any) => ({
        id: u.id,
        email: u.email,
        username: u.username || u.email.split('@')[0],
        role: u.role || 'viewer',
      }));
      setUsers(mappedUsers);
    } catch (err) {
      console.error('Failed to load users:', err);
      // Use mock users as fallback
      setUsers([
        { id: 'user-1', email: 'john@auxilio.cloud', username: 'john_auxilio', role: 'superuser' },
        { id: 'user-2', email: 'naveed.gilani@gmail.com', username: 'naveed', role: 'admin' },
        { id: 'user-3', email: 'abdul.gilani@gmail.com', username: 'abdul', role: 'admin' },
        { id: 'user-4', email: 'brinegar@frii.com', username: 'john_frii', role: 'superuser' },
      ]);
    }
  };

  // Filter users based on search query and exclude already added users
  const filteredUsers = users.filter(user => {
    const query = userSearchQuery.toLowerCase();
    const matchesSearch = user.email.toLowerCase().includes(query) ||
                          user.username.toLowerCase().includes(query);
    const alreadyAdded = (formData.recipients || []).some(r => r.user_id === user.id);
    return matchesSearch && !alreadyAdded;
  });

  const openCreateModal = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      priority: 100,
      is_active: true,
      severity_filter: null,
      category_filter: null,
      stop_processing: false,
      recipients: [],
    });
    setShowEditModal(true);
  };

  const openEditModal = (rule: NotificationRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      priority: rule.priority,
      is_active: rule.is_active,
      severity_filter: rule.severity_filter,
      category_filter: rule.category_filter,
      stop_processing: rule.stop_processing,
      recipients: rule.recipients || [],
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.name) return;
    setSaving(true);

    try {
      if (editingRule) {
        await alertsAPI.updateNotificationRule(editingRule.id, formData);
      } else {
        await alertsAPI.createNotificationRule(formData);
      }
      await loadRules();
      setShowEditModal(false);
    } catch (err) {
      console.error('Failed to save rule:', err);
      // For demo, just close and show the change
      setShowEditModal(false);
      if (!editingRule) {
        setRules([...rules, {
          id: `new-${Date.now()}`,
          name: formData.name || 'New Rule',
          description: formData.description,
          priority: formData.priority || 100,
          is_active: formData.is_active ?? true,
          severity_filter: formData.severity_filter,
          category_filter: formData.category_filter,
          alert_type_filter: null,
          site_filter: null,
          division_filter: null,
          stop_processing: formData.stop_processing ?? false,
          recipients: formData.recipients || [],
        }]);
      } else {
        setRules(rules.map(r => r.id === editingRule.id ? { ...r, ...formData } as NotificationRule : r));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId: string) => {
    try {
      await alertsAPI.deleteNotificationRule(ruleId);
      await loadRules();
    } catch (err) {
      console.error('Failed to delete rule:', err);
      // For demo, just remove from local state
      setRules(rules.filter(r => r.id !== ruleId));
    }
    setShowDeleteConfirm(null);
  };

  const toggleRuleActive = async (rule: NotificationRule) => {
    try {
      await alertsAPI.updateNotificationRule(rule.id, { is_active: !rule.is_active });
      setRules(rules.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch (err) {
      console.error('Failed to toggle rule:', err);
      setRules(rules.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    }
  };

  const toggleSeverityFilter = (severity: string) => {
    const current = formData.severity_filter || [];
    if (current.includes(severity)) {
      const newFilter = current.filter(s => s !== severity);
      setFormData({ ...formData, severity_filter: newFilter.length > 0 ? newFilter : null });
    } else {
      setFormData({ ...formData, severity_filter: [...current, severity] });
    }
  };

  const toggleCategoryFilter = (category: string) => {
    const current = formData.category_filter || [];
    if (current.includes(category)) {
      const newFilter = current.filter(c => c !== category);
      setFormData({ ...formData, category_filter: newFilter.length > 0 ? newFilter : null });
    } else {
      setFormData({ ...formData, category_filter: [...current, category] });
    }
  };

  const addRoleRecipient = (role: string) => {
    const current = formData.recipients || [];
    if (!current.find(r => r.role === role)) {
      setFormData({
        ...formData,
        recipients: [...current, { role, channels: ['in_app', 'email'] }],
      });
    }
  };

  const addUserRecipient = (user: User) => {
    const current = formData.recipients || [];
    if (!current.find(r => r.user_id === user.id)) {
      setFormData({
        ...formData,
        recipients: [...current, { user_id: user.id, user_email: user.email, channels: ['in_app', 'email'] }],
      });
    }
    setUserSearchQuery('');
    setShowUserDropdown(false);
  };

  const removeRecipient = (index: number) => {
    const current = formData.recipients || [];
    setFormData({
      ...formData,
      recipients: current.filter((_, i) => i !== index),
    });
  };

  const toggleRecipientChannel = (index: number, channel: string) => {
    const current = formData.recipients || [];
    const recipient = current[index];
    if (!recipient) return;

    const channels = recipient.channels || [];
    const newChannels = channels.includes(channel)
      ? channels.filter(c => c !== channel)
      : [...channels, channel];

    const newRecipients = [...current];
    newRecipients[index] = { ...recipient, channels: newChannels };
    setFormData({ ...formData, recipients: newRecipients });
  };

  const moveRule = async (rule: NotificationRule, direction: 'up' | 'down') => {
    const index = rules.findIndex(r => r.id === rule.id);
    if (direction === 'up' && index > 0) {
      const newPriority = rules[index - 1].priority - 1;
      try {
        await alertsAPI.updateNotificationRule(rule.id, { priority: newPriority });
        await loadRules();
      } catch (err) {
        // Local update for demo
        const newRules = [...rules];
        newRules[index].priority = newPriority;
        newRules.sort((a, b) => a.priority - b.priority);
        setRules(newRules);
      }
    } else if (direction === 'down' && index < rules.length - 1) {
      const newPriority = rules[index + 1].priority + 1;
      try {
        await alertsAPI.updateNotificationRule(rule.id, { priority: newPriority });
        await loadRules();
      } catch (err) {
        const newRules = [...rules];
        newRules[index].priority = newPriority;
        newRules.sort((a, b) => a.priority - b.priority);
        setRules(newRules);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Configure how alerts are routed to different users and roles. Rules are evaluated in priority order (lowest number first).
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          Add Rule
        </button>
      </div>

      {error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-200">
          {error} - Showing demo data
        </div>
      )}

      {/* Rules List */}
      <div className="space-y-3">
        {rules.map((rule, index) => (
          <div
            key={rule.id}
            className={`border rounded-lg transition-all ${
              rule.is_active
                ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 opacity-60'
            }`}
          >
            {/* Rule Header */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                {/* Priority & Reorder */}
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => moveRule(rule, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400 w-8 text-center">
                    {rule.priority}
                  </span>
                  <button
                    onClick={() => moveRule(rule, 'down')}
                    disabled={index === rules.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Rule Info */}
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">{rule.name}</h4>
                    {!rule.is_active && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                        Disabled
                      </span>
                    )}
                  </div>
                  {rule.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{rule.description}</p>
                  )}

                  {/* Filters */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {/* Severity Filter */}
                    {rule.severity_filter && rule.severity_filter.length > 0 ? (
                      rule.severity_filter.map(s => (
                        <span key={s} className={`text-xs px-2 py-0.5 rounded-full ${severityColors[s]}`}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        Any Severity
                      </span>
                    )}

                    <span className="text-gray-300 dark:text-gray-600">|</span>

                    {/* Category Filter */}
                    {rule.category_filter && rule.category_filter.length > 0 ? (
                      rule.category_filter.map(c => (
                        <span key={c} className={`text-xs px-2 py-0.5 rounded-full ${categoryColors[c]}`}>
                          {c.charAt(0).toUpperCase() + c.slice(1)}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        Any Category
                      </span>
                    )}
                  </div>

                  {/* Recipients */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex flex-wrap gap-1">
                      {/* Role recipients */}
                      {rule.recipients.filter(r => r.role).map((r, i) => (
                        <span
                          key={`role-${i}`}
                          className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 rounded flex items-center gap-1"
                        >
                          <UserGroupIcon className="w-3 h-3" />
                          {RoleInfoMap[r.role as keyof typeof RoleInfoMap]?.name || r.role}
                          <span className="text-purple-600 dark:text-purple-500">
                            ({r.channels.map(c => channelLabels[c] || c).join(', ')})
                          </span>
                        </span>
                      ))}
                      {/* User recipients */}
                      {rule.recipients.filter(r => r.user_id || r.user_email).map((r, i) => (
                        <span
                          key={`user-${i}`}
                          className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded flex items-center gap-1"
                        >
                          <UserIcon className="w-3 h-3" />
                          {r.user_email || 'Unknown'}
                          <span className="text-blue-600 dark:text-blue-500">
                            ({r.channels.map(c => channelLabels[c] || c).join(', ')})
                          </span>
                        </span>
                      ))}
                      {rule.recipients.length === 0 && (
                        <span className="text-xs text-gray-400 italic">No recipients</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleRuleActive(rule)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    rule.is_active ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      rule.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <button
                  onClick={() => openEditModal(rule)}
                  className="p-2 text-gray-400 hover:text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(rule.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {rules.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <BellAlertIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No notification rules configured</p>
          <p className="text-sm mt-1">Click "Add Rule" to create your first routing rule</p>
        </div>
      )}

      {/* Edit/Create Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingRule ? 'Edit Notification Rule' : 'Create Notification Rule'}
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Name & Description */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Critical Alerts - All Admins"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Route all critical alerts to administrators"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Priority (lower = higher priority)
                    </label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 100 })}
                      min={1}
                      max={999}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Rule is active</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Severity Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Severity Filter (leave empty for any severity)
                </label>
                <div className="flex flex-wrap gap-2">
                  {SEVERITY_OPTIONS.map(severity => (
                    <button
                      key={severity}
                      onClick={() => toggleSeverityFilter(severity)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                        (formData.severity_filter || []).includes(severity)
                          ? severityColors[severity]
                          : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                      }`}
                    >
                      {severity.charAt(0).toUpperCase() + severity.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category Filter (leave empty for any category)
                </label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map(category => (
                    <button
                      key={category}
                      onClick={() => toggleCategoryFilter(category)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                        (formData.category_filter || []).includes(category)
                          ? categoryColors[category]
                          : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                      }`}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Recipients
                </label>

                {/* Add by Role */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                    <UserGroupIcon className="w-4 h-4" />
                    Add by Role (notifies all users with this role)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ROLE_OPTIONS.filter(role => !(formData.recipients || []).find(r => r.role === role)).map(role => (
                      <button
                        key={role}
                        onClick={() => addRoleRecipient(role)}
                        className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        + {RoleInfoMap[role as keyof typeof RoleInfoMap]?.name || role}
                      </button>
                    ))}
                    {ROLE_OPTIONS.filter(role => !(formData.recipients || []).find(r => r.role === role)).length === 0 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">All roles added</span>
                    )}
                  </div>
                </div>

                {/* Add by User */}
                <div className="mb-4" ref={userSearchRef}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                    <UserIcon className="w-4 h-4" />
                    Add Specific Users
                  </p>
                  <div className="relative">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => {
                          setUserSearchQuery(e.target.value);
                          setShowUserDropdown(true);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                        placeholder="Search users by email or username..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    {/* User dropdown */}
                    {showUserDropdown && filteredUsers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredUsers.slice(0, 10).map(user => (
                          <button
                            key={user.id}
                            onClick={() => addUserRecipient(user)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                          >
                            <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium text-teal-600 dark:text-teal-400">
                                {user.username.substring(0, 2).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.username}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                            </div>
                            <span className="ml-auto text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                              {RoleInfoMap[user.role as keyof typeof RoleInfoMap]?.name || user.role}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {showUserDropdown && userSearchQuery && filteredUsers.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No users found matching "{userSearchQuery}"
                      </div>
                    )}
                  </div>
                </div>

                {/* Recipient list */}
                <div className="space-y-2">
                  {/* Role recipients */}
                  {(formData.recipients || []).filter(r => r.role).map((recipient, index) => {
                    const actualIndex = (formData.recipients || []).findIndex(r => r === recipient);
                    return (
                      <div
                        key={`role-${index}`}
                        className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <UserGroupIcon className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {RoleInfoMap[recipient.role as keyof typeof RoleInfoMap]?.name || recipient.role}
                          </span>
                          <span className="text-xs text-purple-600 dark:text-purple-400">(Role)</span>
                          <div className="flex gap-1">
                            {CHANNEL_OPTIONS.map(channel => (
                              <button
                                key={channel}
                                onClick={() => toggleRecipientChannel(actualIndex, channel)}
                                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                  recipient.channels.includes(channel)
                                    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
                                    : 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                }`}
                              >
                                {channelLabels[channel]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => removeRecipient(actualIndex)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}

                  {/* User recipients */}
                  {(formData.recipients || []).filter(r => r.user_id).map((recipient, index) => {
                    const actualIndex = (formData.recipients || []).findIndex(r => r === recipient);
                    return (
                      <div
                        key={`user-${index}`}
                        className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <UserIcon className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {recipient.user_email}
                          </span>
                          <span className="text-xs text-blue-600 dark:text-blue-400">(User)</span>
                          <div className="flex gap-1">
                            {CHANNEL_OPTIONS.map(channel => (
                              <button
                                key={channel}
                                onClick={() => toggleRecipientChannel(actualIndex, channel)}
                                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                  recipient.channels.includes(channel)
                                    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
                                    : 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                }`}
                              >
                                {channelLabels[channel]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => removeRecipient(actualIndex)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {(formData.recipients || []).length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                    No recipients added. Add roles or search for specific users above.
                  </p>
                )}
              </div>

              {/* Stop Processing */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.stop_processing}
                    onChange={(e) => setFormData({ ...formData, stop_processing: e.target.checked })}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Stop processing lower-priority rules when this rule matches
                  </span>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name || saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    {editingRule ? 'Save Changes' : 'Create Rule'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Rule</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete this notification rule? Alerts will no longer be routed according to this rule.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertNotificationRules;
